'use client'

import { useEffect, useRef } from 'react'

import { useLogger } from '@/hooks/use-logger'
import { reportEcommerceEvent, type GtmEcommerce } from '@/services/gtm'

interface C2CViewItemListProps {
    /** Built on the server from the same values the body renders, so the browser is handed
     *  a finished report rather than the route data to rebuild it from. */
    report: GtmEcommerce
}

/**
 * Reports the route a C2C page is showing, as the advertising platforms' route-list
 * impression. The page's tags run at container load, so the report is what a tag reads;
 * making it in an effect rather than during render keeps it off the server pass, where
 * there is no queue.
 *
 * Guarded by the route it last reported rather than by "have I run": a client transition
 * from one route page to another is a new impression and owes its own report, while the
 * re-renders in between owe nothing.
 */
export function C2CViewItemList({ report }: C2CViewItemListProps) {
    const logger = useLogger({ module: 'c2c-view-item-list' })
    const reported = useRef<string>(undefined)

    useEffect(() => {
        const route = report.items[0]?.item_id
        if (reported.current === route) {
            return
        }
        reported.current = route
        const outcome = reportEcommerceEvent('view_item_list', report)
        // A lost report is invisible everywhere else: the queue swallows it, the page
        // renders the same, and the impression simply never reaches the platforms.
        if (outcome === 'failed') {
            logger.warn('c2c view_item_list not reported')
        }
    }, [report, logger])

    return null
}
